// Constantes & helpers du constructeur de site (Phase 2 — mise en page structurée)

export const uid = () => Math.random().toString(36).slice(2, 10);
export const clone = (o) => JSON.parse(JSON.stringify(o));

// Réglages par défaut de chaque bloc de contenu (feuille)
export const LEAF_DEFAULTS = {
  heading: { text: "Nouveau titre", level: "2", align: "left" },
  richtext: { html: "<p>Votre texte ici…</p>", align: "left" },
  image: { url: "", alt: "", caption: "", width: "full", align: "center" },
  gallery: { images: [], columns: "3" },
  button: { label: "En savoir plus", href: "/", style: "primary", align: "left" },
  video: { url: "" },
  divider: {},
  spacer: { size: 32 },
  widget: { widget: "devis_cta" },
};

// Blocs métier (widgets) : le code reste figé, l'admin ne fait que les placer.
export const WIDGET_OPTIONS = [
  { value: "devis_cta", label: "Appel au devis" },
  { value: "partners_grid", label: "Partenaires locaux" },
  { value: "offers_grid", label: "Bons plans / offres" },
  { value: "events", label: "Vie locale (événements)" },
];
export const WIDGET_LABELS = WIDGET_OPTIONS.reduce((a, o) => ({ ...a, [o.value]: o.label }), {});

export const SECTION_DATA_DEFAULT = { bg: "", paddingY: "md", maxWidth: "normal" };

// Largeurs d'aperçu responsive (Phase 3)
export const DEVICE_WIDTHS = { desktop: "100%", tablet: "768px", mobile: "390px" };

// Classes utilitaires issues des réglages de style fins (Phase 3)
const MT = { none: "", sm: "mt-2", md: "mt-5", lg: "mt-10" };
const MB = { none: "", sm: "mb-2", md: "mb-5", lg: "mb-10" };
const RADIUS = { none: "", sm: "rounded-md overflow-hidden", md: "rounded-xl overflow-hidden", lg: "rounded-3xl overflow-hidden", full: "rounded-full overflow-hidden" };
const SHADOW = { none: "", sm: "shadow-sm", md: "shadow-md", lg: "shadow-xl" };
const ANIM = { fade: "cms-anim-fade", up: "cms-anim-up" };

export function styleClasses(d = {}) {
  return [
    MT[d.mt] || "", MB[d.mb] || "", RADIUS[d.radius] || "", SHADOW[d.shadow] || "",
    ANIM[d.anim] || "", d.hideMobile ? "hidden md:block cms-hide-mobile" : "",
  ].filter(Boolean).join(" ");
}
export function styleInline(d = {}) {
  const st = {};
  if (d.textColor) st.color = d.textColor;
  return st;
}

export const newLeaf = (type) => ({ id: uid(), type, data: { ...(LEAF_DEFAULTS[type] || {}) } });

export const newColumn = () => ({ id: uid(), blocks: [] });

export const newSection = (nbCols = 1) => ({
  id: uid(),
  type: "section",
  data: { ...SECTION_DATA_DEFAULT },
  columns: Array.from({ length: Math.max(1, Math.min(4, nbCols)) }, () => newColumn()),
});

// Bibliothèque de blocs (colonne de gauche)
export const BLOCK_LIBRARY = [
  {
    group: "Contenu",
    items: [
      { type: "heading", label: "Titre", icon: "Heading" },
      { type: "richtext", label: "Texte", icon: "Type" },
      { type: "image", label: "Image", icon: "Image" },
      { type: "gallery", label: "Galerie", icon: "Images" },
      { type: "button", label: "Bouton", icon: "MousePointerClick" },
      { type: "video", label: "Vidéo", icon: "Video" },
      { type: "divider", label: "Séparateur", icon: "Minus" },
      { type: "spacer", label: "Espace", icon: "MoveVertical" },
    ],
  },
  {
    group: "Blocs métier",
    items: [
      { type: "widget", label: "Bloc métier", icon: "Puzzle" },
    ],
  },
];

export const LEAF_LABELS = BLOCK_LIBRARY.flatMap((g) => g.items).reduce(
  (acc, it) => ({ ...acc, [it.type]: it.label }), {},
);

// Normalise les `blocks` stockés vers une structure de sections.
// - Si déjà des sections → on garantit la présence des colonnes/ids.
// - Si ancien format "plat" (blocs feuilles à la racine) → on enveloppe dans
//   une section d'une colonne (migration transparente, sans perte).
export function normalizeSections(blocks) {
  const arr = Array.isArray(blocks) ? blocks : [];
  if (arr.length === 0) return [];
  const allSections = arr.every((b) => b && b.type === "section");
  if (allSections) {
    return arr.map((s) => ({
      id: s.id || uid(),
      type: "section",
      data: { ...SECTION_DATA_DEFAULT, ...(s.data || {}) },
      columns: (s.columns && s.columns.length ? s.columns : [newColumn()]).map((c) => ({
        id: c.id || uid(),
        blocks: (c.blocks || []).map((b) => ({ id: b.id || uid(), type: b.type, data: b.data || {} })),
      })),
    }));
  }
  return [{
    id: uid(), type: "section", data: { ...SECTION_DATA_DEFAULT },
    columns: [{ id: uid(), blocks: arr.map((b) => ({ id: b.id || uid(), type: b.type, data: b.data || {} })) }],
  }];
}

// Localise un bloc feuille dans les sections → { sid, cid, index } ou null
export function findBlock(sections, blockId) {
  for (const s of sections) {
    for (const c of s.columns) {
      const index = c.blocks.findIndex((b) => b.id === blockId);
      if (index !== -1) return { sid: s.id, cid: c.id, index };
    }
  }
  return null;
}
